import type { CountryCode } from 'libphonenumber-js/max';
import { ParseError, parsePhoneNumberWithError } from 'libphonenumber-js/max';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { Area, Country, Locale } from '@/types/regionTypes';

import { LocaleConfiguration } from '@/lib/configuration/configuration';
import { getRegionList } from '@/lib/countries';
import type { NameType } from '@/lib/forms/schemas';
import { formatNationalByPhonePrefix, getExamplePhone } from '@/lib/phone';
import { formMessages } from '@/messages/forms';

export interface CountryOption {
  readonly code: string;
  readonly label: string;
  readonly locales?: Locale[];
  readonly name: string;
  readonly slug: string;
  readonly value: string;
}

export interface LanguageOption {
  readonly code?: string;
  readonly contentful?: string;
  readonly label: string;
  readonly locale?: string;
  readonly name?: string;
  readonly sfCode?: string;
  readonly slug?: string;
  readonly value: string;
}

export const convertCountrySelectOptions = (areas: Area[]): CountryOption[] => {
  const options: CountryOption[] = [];

  areas.forEach((area) => {
    const { countries } = area;
    countries!.forEach((country) => {
      const { code, locales, name, slug } = country;

      options.push({
        name,
        slug,
        code,
        label: name,
        value: code,
        locales,
      });
    });
  });

  return options;
};

export const getCountrySelectOptions = (): CountryOption[] => {
  const areas = getRegionList() as Area[];
  return convertCountrySelectOptions(areas);
};

export const getLocalesBySlug = (slug: string): LanguageOption[] => {
  const country: CountryOption | undefined = getCountrySelectOptions().find(
    (country) => country?.slug === slug,
  );
  const languages = LocaleConfiguration.getInstance().getLanguages();

  const _locales = !isEmpty(country?.locales)
    ? country?.locales
    : [languages.enUS];

  return (
    _locales?.map((item) => ({
      label: item.name,
      value: item.slug,
      ...item,
    })) ?? []
  );
};

export const getLocalesByCountryCode = (code: string): LanguageOption[] => {
  const country: CountryOption | undefined = getCountrySelectOptions().find(
    (country) => country?.code === code,
  );

  const languages = LocaleConfiguration.getInstance().getLanguages();
  const _locales = !isEmpty(country?.locales)
    ? country?.locales
    : [languages.enUS];

  return (
    _locales?.map((item) => ({
      label: item.name,
      value: item.slug,
      ...item,
    })) ?? []
  );
};

export const topOrder = -7;

export const getNameFieldOrder = (
  country: Country | string,
  locale: Locale | string,
  type: NameType,
): number => {
  const countryCode = typeof country === 'object' ? country.code : country;
  const localeSlug = typeof locale === 'object' ? locale.slug : locale;

  if (countryCode !== 'JP') {
    return 0;
  }
  if (localeSlug === 'ja' || localeSlug === 'zh') {
    switch (type) {
      case 'lastKanji':
        return -6;
      case 'firstKanji':
        return -5;
      case 'last':
        return -4;
      case 'first':
        return -3;
      case 'lastKatakana':
        return -2;
      case 'firstKatakana':
        return -1;
    }
  } else if (localeSlug === 'en') {
    switch (type) {
      case 'first':
        return -6;
      case 'last':
        return -5;
      case 'firstKanji':
        return -4;
      case 'lastKanji':
        return -3;
      case 'firstKatakana':
        return -2;
      case 'lastKatakana':
        return -1;
    }
  }
  return 0;
};

export const useExamplePhone = (formCtx: UseFormReturn<any>) => {
  const { formatMessage } = useIntl();
  const [examplePhone, setExamplePhone] = useState<string>('');

  const phone = formCtx.watch('phone');
  const phonePrefix = formCtx.watch('phonePrefix');
  const phoneCode = phonePrefix.code as CountryCode;

  useEffect(() => {
    if (phone && phonePrefix?.value) {
      try {
        const phoneNumber = parsePhoneNumberWithError(phone, phoneCode);
        formCtx.setValue(
          'phone',
          formatNationalByPhonePrefix(
            phonePrefix.value,
            phoneNumber.formatNational(),
          ),
        );
        formCtx.clearErrors('phone');
      } catch (error) {
        if (error instanceof ParseError) {
          formCtx.setError('phone', {
            message: formatMessage(formMessages.invalidPhoneNumber),
          });
        } else {
          formCtx.setError('phone', {
            message: formatMessage(formMessages.technicalError),
          });
        }
      }
    }
  }, [phone, phonePrefix, phoneCode, formCtx, formatMessage]);

  useEffect(() => {
    setExamplePhone(getExamplePhone(phoneCode));
  }, [phoneCode]);

  return examplePhone;
};

type NameFieldComponentChunk = { component: React.ReactNode; order: number };

export const SortedNameFields = ({
  arr,
}: {
  arr: NameFieldComponentChunk[];
}) => {
  return (
    <>
      {[...arr]
        .sort(function (a, b) {
          return a.order - b.order;
        })
        .map((item) => item.component)}
    </>
  );
};
